import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { Button, UncontrolledTooltip } from 'reactstrap';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { filter, head } from 'lodash';

import Icon from '../../common/Icon';
import { createForm } from '../../../models/Form';
import { createFormField } from '../../../models/FormField';
import FieldError from '../../form/FieldError';
import QuantityFieldGroup from '../../common/QuantityFieldGroup';
import ProductAdditionalService from '../../product/ProductAdditionalService';
import { modelOf } from '../../../prop-types';
import Product from '../../../models/Product';
import ServiceOptionType from '../../../types/ServiceOptionType';
import { productCountValid, quantityValid } from '../../../util/formValidators';
import globalTranslations from '../../../i18n/globalTranslations';
import CartStore from '../../../store/CartStore';
import CategoryStore from '../../../store/CategoryStore';
import RequestState from '../../../types/RequestState';
import AddToCartServices from '../AddToCartServices';
import UIStore from '../../../store/UIStore';
import ConfigStore from '../../../store/ConfigStore';

@observer
export class AddToCart extends Component {
  constructor(props) {
    super(props);

    this.state = {
      buttonMounted: false,
      servicesInitialized: false,
      selectedServices: []
    };

    this.form = null;
    this.formName = 'AddToCartForm';
    this.buttonElement = null;

    this.initializeForm({ ifUpdated: false });
  }

  componentDidMount() {
    if (this.props.onUpdateProductQuantity) {
      this.props.onUpdateProductQuantity(this.getQuantity());
    }
  }

  componentDidUpdate(prevProps) {
    const { withServices, maxQuantity } = this.props;

    if (
      prevProps.withServices !== withServices ||
      prevProps.maxQuantity !== maxQuantity ||
      (prevProps.withServices &&
        withServices &&
        !this.state.servicesInitialized)
    ) {
      this.initializeForm({ ifUpdated: true });
    }
  }

  initializeForm = ({ ifUpdated }) => {
    const {
      quantity,
      maxQuantity,
      quantityStep,
      product,
      withServices,
      withQuantityPicker
    } = this.props;

    this.form = null;
    this.formFields = {};

    this.formFields.quantity = createFormField(
      {
        value: withQuantityPicker ? String(quantityStep) : quantity
      },
      [
        quantityValid(maxQuantity, quantityStep),
        productCountValid(quantityStep)
      ]
    );

    if (withServices && product.additionalServices.length > 0) {
      ifUpdated && this.updateState();

      product.additionalServices.forEach((service) => {
        this.createTextInputOption(service);

        this.createRadioButtonOptions(service);

        this.createCheckboxOptions(service);

        this.createDropdownOptions(service);

        this.createSelectedService(service);
      });

      this.createPostalCodeField();
    }

    this.form = createForm(this.formFields);
  };

  updateState = () => {
    // Use state because component tries to render before dynamic form is reinitialized.
    this.setState({
      servicesInitialized: true
    });
  };

  createTextInputOption = (service) => {
    if (service.enable_text_input) {
      const textFieldName = `text_${service.id}`;

      this.formFields[textFieldName] = createFormField({});
    }
  };

  createRadioButtonOptions = (service) => {
    const radioButtonOptions = this.getRadioButtonOptions(service);

    if (radioButtonOptions.length > 0) {
      const radioFieldName = `radioOptions_${service.id}`;
      this.formFields[radioFieldName] = createFormField({});
      this.formFields[radioFieldName].setValue(
        head(radioButtonOptions).product_id
      );
    }
  };

  getRadioButtonOptions(service) {
    return service.options.filter(
      (option) => option.type.indexOf(ServiceOptionType.RADIO) !== -1
    );
  }

  createCheckboxOptions = (service) => {
    const checkboxOptions = this.getCheckboxOptions(service);

    if (checkboxOptions.length > 0) {
      const checkboxFieldName = `checkboxOptions_${service.id}`;
      this.formFields[checkboxFieldName] = createFormField({});
    }
  };

  getCheckboxOptions = (service) => {
    return service.options.filter(
      (option) => option.type.indexOf(ServiceOptionType.CHECKBOX) !== -1
    );
  };

  createDropdownOptions = (service) => {
    const dropdownOptions = this.getDropdownOptions(service);

    if (dropdownOptions.length > 0) {
      const dropdownFieldName = `dropdownOptions_${service.id}`;
      this.formFields[dropdownFieldName] = createFormField({});
      this.formFields[dropdownFieldName].setValue(
        head(dropdownOptions).product_id
      );
    }
  };

  getDropdownOptions = (service) => {
    return service.options.filter(
      (option) => option.type.indexOf(ServiceOptionType.DROPDOWN) !== -1
    );
  };

  createSelectedService = (service) => {
    const selectedService = this.ifCreateService(service);

    if (selectedService) {
      const serviceObject = this.createService(
        service.id,
        service.product_id,
        true,
        '',
        {},
        service.text_max_length,
        service.text_required
      );

      this.state.selectedServices.push(serviceObject);
    }
  };

  ifCreateService = (service) =>
    (service.is_required || service.selected_by_default) &&
    !service.requires_area_validation;

  createPostalCodeField = () => {
    const { product } = this.props;

    if (product.ifServiceRequiresPostalCodeValidation()) {
      const postalCodeFieldName = `postalCode_${product.id}`;
      this.formFields[postalCodeFieldName] = createFormField({});
    }
  };

  handleSubmit = (e) => {
    const { categoryStore, product } = this.props;
    e.preventDefault();
    this.form.validate();
    const postalCodeField = this.getPostalCodeField();
    if (this.form.valid && postalCodeField && postalCodeField.value) {
      product
        .validateAdditionalServices({
          category: categoryStore.activeCategory.id,
          postalCode: postalCodeField.value
        })
        .then(this.handleValidatedServices);
    }
  };

  handleValidatedServices = () => {
    const { product } = this.props;

    const postalCodeField = this.getPostalCodeField();
    const validServices = product.getValidatedServices({
      productId: product.id,
      postalCode: postalCodeField.value
    });

    validServices.forEach((service) => {
      const isRequired = service.is_required;
      const postalCodeIsRequired = service.requires_area_validation;
      const selectedByDefault = service.selected_by_default;

      if (postalCodeIsRequired && (isRequired || selectedByDefault)) {
        const serviceObject = this.createService(
          service.id,
          service.product_id,
          service.valid_postal_code,
          '',
          {},
          service.text_max_length,
          service.text_required
        );

        this.handleAdditionalServiceClick(serviceObject);
      }
    });
  };

  getPostalCodeValidationState = () => {
    const { product } = this.props;

    const postalCodeField = this.getPostalCodeField();

    if (!postalCodeField || !postalCodeField.value) {
      return { loading: false, ifValidatedService: false };
    }

    const serviceIsLoading =
      product.validatedServicesState.get(
        `${product.id}--${postalCodeField.value}`
      ) === RequestState.LOADING;

    const ifValidatedService = product.ifValidatedServicesExists({
      productId: product.id,
      postalCode: postalCodeField.value
    });

    return { serviceIsLoading, ifValidatedService };
  };

  getPostalCodeField = () => {
    const { product } = this.props;

    if (!product.ifServiceRequiresPostalCodeValidation()) {
      return;
    }

    return this.form.fields.get(`postalCode_${product.id}`);
  };

  createService = (
    id,
    productId,
    isSelected,
    text,
    options,
    textMaxLength,
    textRequired
  ) => {
    return {
      id,
      productId,
      isSelected,
      text,
      options,
      textMaxLength,
      textRequired
    };
  };

  validText = (service) => {
    if (service.textRequired === true && service.text.length === 0) {
      return false;
    }

    return !(
      service.textMaxLength && service.text.length > service.textMaxLength
    );
  };

  servicesAreValid = () => {
    const { product } = this.props;
    let valid = true;

    const requiredServiceIds = product.additionalServices
      .filter((service) => service.is_required)
      .map((service) => service.id);

    const selectedService = this.state.selectedServices.filter(
      (service) => service.isSelected
    );

    // Make sure required services are selected.
    const selectedServiceIds = selectedService.map((service) => service.id);
    valid = requiredServiceIds.every((id) => selectedServiceIds.includes(id));

    selectedService.forEach((service) => {
      if (!this.validText(service)) {
        valid = false;
      }
    });

    return valid;
  };

  handleAddToCartClick = (e) => {
    const {
      disabled,
      quantity,
      product,
      configStore,
      uiStore,
      loading
    } = this.props;
    const ifServices =
      product.additionalServices && product.additionalServices.length;
    const ifAdditionalServicesModalEnabled =
      configStore.productPage.enableAdditionalServicesModal;
    const servicesAreLoading =
      product.additionalServicesState === RequestState.LOADING;
    const toggleServicesModal = uiStore.additionalServicesModal.toggle;

    if ((ifAdditionalServicesModalEnabled && servicesAreLoading) || loading) {
      return;
    }

    if (!disabled) {
      e.preventDefault();
      ifAdditionalServicesModalEnabled && ifServices
        ? toggleServicesModal()
        : this.addProductToCart(quantity);
    }
  };

  handleModalAddToCartClick = (e) => {
    const { disabled, quantity, uiStore } = this.props;
    const toggleServicesModal = uiStore.additionalServicesModal.toggle;

    if (!disabled) {
      e.preventDefault();
      toggleServicesModal();
      this.addProductToCart(quantity);
    }
  };

  addProductToCart = (quantity) => {
    const { onAddToCartClick } = this.props;
    this.form.validate();
    if (
      this.form.valid &&
      this.servicesAreValid() &&
      onAddToCartClick &&
      this.getQuantity() === quantity
    ) {
      onAddToCartClick(this.getQuantity(), this.getServices());
    }
  };

  handleAdditionalServiceClick = (value) => {
    const { selectedServices } = this.state;

    if (
      selectedServices.length === 0 ||
      filter(selectedServices, (val) => val.id === value.id).length === 0
    ) {
      selectedServices.push(value);
    } else {
      selectedServices.forEach((service) => {
        if (service.id === value.id) {
          service.isSelected = value.isSelected;
          service.text = value.text;
          service.options = value.options;
        }
      });
    }

    this.setState({ selectedServices });
  };

  getQuantity = () => Number(this.form.fields.get('quantity').value);

  getOptionProductIds = (options) => {
    const optionsArray = [];

    if (options.checkbox) {
      options.checkbox.forEach((value) => {
        optionsArray.push(value);
      });
    }
    return optionsArray;
  };

  getProductIds = (service) => {
    const ids = [];

    if (service.productId) {
      ids.push({
        service_id: service.id,
        id: service.productId,
        addText: true
      });
    }

    if (service.options) {
      let optionIds = this.getOptionProductIds(service.options);

      optionIds.forEach((id) => {
        ids.push({
          id: id,
          addText: false
        });
      });
    }
    if (this.form.fields.get(`radioOptions_${service.id}`)) {
      ids.push({
        id: this.form.fields.get(`radioOptions_${service.id}`).value,
        addText: false
      });
    }
    if (this.form.fields.get(`dropdownOptions_${service.id}`)) {
      ids.push({
        id: this.form.fields.get(`dropdownOptions_${service.id}`).value,
        addText: false
      });
    }

    return ids;
  };

  getServices = () => {
    const { selectedServices } = this.state;
    let servicesToCart = [];

    const services = selectedServices.filter((service) => service.isSelected);

    services.forEach((service) => {
      const productIds = this.getProductIds(service);

      productIds.map((id) =>
        servicesToCart.push({
          service_id: id.service_id,
          products_id: id.id,
          text: id.addText ? service.text : ''
        })
      );
    });

    return servicesToCart;
  };

  setButtonElement = (ref) => {
    this.buttonElement = ref;
    this.setState({ buttonMounted: true });
  };

  getServiceInfo = (id) => {
    const match = this.state.selectedServices
      .map((service) => service)
      .filter((service) => service.id === id);

    return match.length !== 0 ? match : null;
  };

  onUpdateProductQuantity = (quantity) => {
    const { cartStore, onUpdateProductQuantity } = this.props;
    if (onUpdateProductQuantity) {
      cartStore.updateProductQuantity(quantity);
      onUpdateProductQuantity(quantity);
    }
  };

  renderProductAdditionalServices = (service, index) => {
    const { withTax, product } = this.props;
    const data = head(this.getServiceInfo(service.id));
    const textField = this.form.fields.get(`text_${service.id}`);
    const checkBoxField = this.form.fields.get(`checkboxOptions_${service.id}`);
    const radioField = this.form.fields.get(`radioOptions_${service.id}`);
    const dropdownField = this.form.fields.get(`dropdownOptions_${service.id}`);
    const postalCodeField = this.getPostalCodeField();

    return (
      <ProductAdditionalService
        key={`${service.id}-${index}`}
        service={service}
        withTax={withTax}
        formName={this.formName}
        isSelected={data ? data.isSelected : false}
        text={data ? data.text : ''}
        textField={textField}
        checkboxField={checkBoxField}
        radioField={radioField}
        dropdownField={dropdownField}
        postalCodeField={postalCodeField}
        selectedOptions={data ? data.options : null}
        handleSelectedServices={this.handleAdditionalServiceClick}
        product={product}
      />
    );
  };

  ifRenderServices = () => {
    const { disabled, withServices } = this.props;
    return !disabled && withServices && this.state.servicesInitialized;
  };

  ifButtonDisabled = () => {
    const { configStore, product, quantity, minOrderQuantity } = this.props;
    const ifRequiresPostalCodeValidation = product.ifServiceRequiresPostalCodeValidation();
    const ifRequired = product.ifServiceIsRequiredAndRequiresPostalCodeValidation();
    const { ifValidatedService } = this.getPostalCodeValidationState();
    const ifAdditionalServicesModalEnabled =
      configStore.productPage.enableAdditionalServicesModal;

    if (ifAdditionalServicesModalEnabled) {
      return this.form.valid === false || quantity < minOrderQuantity;
    }

    return (
      this.ifOrderDisabled() ||
      (ifRequiresPostalCodeValidation && ifRequired && !ifValidatedService)
    );
  };

  ifOrderDisabled = () => {
    const { quantity, minOrderQuantity } = this.props;

    return (
      this.form.valid === false ||
      this.servicesAreValid() === false ||
      quantity < minOrderQuantity
    );
  };

  render() {
    const {
      disabled,
      ifWebStore,
      loading,
      maxQuantity,
      notAllowed,
      product,
      quantityStep,
      scrollToServices,
      tooltipMessage,
      unit,
      withQuantityPicker
    } = this.props;
    const addToCartButtonClassNames = classNames(
      {
        // We add disabled as a className instead of a prop to handle disabled button onClick -events
        disabled,
        'AddToCart__button--not-allowed': notAllowed
      },
      'AddToCart__button'
    );
    const quantityField = this.form.fields.get('quantity');

    const servicesAreLoading =
      product.additionalServicesState === RequestState.LOADING;

    return (
      <div className="AddToCart">
        <div className="form-inline">
          {this.ifRenderServices() && (
            <AddToCartServices
              services={product.additionalServices}
              onAddToCart={this.handleModalAddToCartClick}
              renderService={(service, index) =>
                this.renderProductAdditionalServices(service, index)
              }
              disabled={servicesAreLoading || this.ifOrderDisabled()}
              scrollToServices={scrollToServices}
              ifValidForm={this.form.valid}
              onSubmit={this.handleSubmit}
              onChange={this.handleAdditionalServiceClick}
              postalCodeField={this.getPostalCodeField()}
              product={product}
            />
          )}
          {withQuantityPicker && (
            <QuantityFieldGroup
              key={product.id}
              className="AddToCart__quantity-group"
              disabled={disabled || servicesAreLoading}
              field={quantityField}
              maxQuantity={maxQuantity}
              quantityStep={quantityStep}
              unit={unit}
              onUpdateProductQuantity={this.onUpdateProductQuantity}
            />
          )}
          {ifWebStore && (
            <Button
              color="primary"
              className={addToCartButtonClassNames}
              disabled={
                loading || servicesAreLoading || this.ifButtonDisabled()
              }
              onClick={this.handleAddToCartClick}
              innerRef={this.setButtonElement}
            >
              <Icon
                name={loading ? 'circle-o-notch' : 'shopping-cart'}
                spin={loading}
              />
              <FormattedMessage {...globalTranslations.addToCartButton} />
            </Button>
          )}
          <FieldError field={this.form.fields.get('quantity')} />
        </div>
        {tooltipMessage &&
          this.state.buttonMounted && (
            <UncontrolledTooltip placement="top" target={this.buttonElement}>
              {tooltipMessage}
            </UncontrolledTooltip>
          )}
      </div>
    );
  }
}

AddToCart.propTypes = {
  cartStore: modelOf(CartStore).isRequired,
  categoryStore: modelOf(CategoryStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  onAddToCartClick: PropTypes.func.isRequired,
  maxQuantity: PropTypes.number.isRequired,
  quantityStep: PropTypes.number.isRequired,
  product: modelOf(Product).isRequired,
  onUpdateProductQuantity: PropTypes.func,
  scrollToServices: PropTypes.func,
  tooltipMessage: PropTypes.node,
  unit: PropTypes.string,
  quantity: PropTypes.number,
  minOrderQuantity: PropTypes.number,
  disabled: PropTypes.bool,
  notAllowed: PropTypes.bool,
  loading: PropTypes.bool,
  withTax: PropTypes.bool,
  withServices: PropTypes.bool,
  withQuantityPicker: PropTypes.bool,
  ifWebStore: PropTypes.bool
};

AddToCart.defaultProps = {
  ifWebStore: true,
  withQuantityPicker: true
};

export default inject('cartStore', 'categoryStore', 'configStore', 'uiStore')(
  AddToCart
);



// WEBPACK FOOTER //
// ./src/components/cart/AddToCart/index.jsx